import React from "react";
import {
  AspectRatio,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useTheme } from "@mui/joy/styles";

import { DarshanMedia, DarshanType, fetchDarshanByType } from "../api";

const DARSHAN_TYPES: DarshanType[] = ["Sringar", "Mangala", "Festival"];

type DayCard = { date: string; cover: DarshanMedia };

export default function DarshanGallery() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const heroBackground = isDark
    ? "linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)"
    : "radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)";
  const heroOverlay = isDark
    ? "radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)"
    : "radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)";
  const heroTitle = isDark ? "rgba(248,250,252,0.94)" : "text.primary";
  const heroBody = isDark ? "rgba(226,232,240,0.8)" : "text.secondary";
  const panelWarm = isDark
    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))"
    : "linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))";
  const panelCool = isDark
    ? "linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))"
    : "linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))";
  const navigate = useNavigate();
  const [cards, setCards] = React.useState<Record<DarshanType, DayCard[]>>({
    Sringar: [],
    Mangala: [],
    Festival: [],
  });
  const [loading, setLoading] = React.useState<Record<DarshanType, boolean>>({
    Sringar: true,
    Mangala: true,
    Festival: true,
  });
  const [showCount, setShowCount] = React.useState<Record<DarshanType, number>>({
    Sringar: 4,
    Mangala: 4,
    Festival: 4,
  });

  const loadType = React.useCallback(async (type: DarshanType) => {
    setLoading((previous) => ({ ...previous, [type]: true }));
    try {
      const listRaw = await fetchDarshanByType(type, { days: 45 });
      const list: DarshanMedia[] = Array.isArray(listRaw) ? listRaw : [];
      const byDate = list.reduce<Record<string, DarshanMedia[]>>((acc, item) => {
        (acc[item.date] ||= []).push(item);
        return acc;
      }, {});

      const results: DayCard[] = Object.keys(byDate)
        .sort((a, b) => (a > b ? -1 : 1))
        .map((date) => {
          const entries = byDate[date];
          const cover = entries.find((entry) => entry.mediaType === "image") ?? entries[0];
          return { date, cover };
        });

      setCards((current) => ({ ...current, [type]: results }));
    } catch (error) {
      console.error("Failed to load darshan for", type, error);
      setCards((current) => ({ ...current, [type]: [] }));
    } finally {
      setLoading((previous) => ({ ...previous, [type]: false }));
    }
  }, []);

  React.useEffect(() => {
    DARSHAN_TYPES.forEach((type) => {
      void loadType(type);
    });
  }, [loadType]);

  const sections = React.useMemo(
    () =>
      DARSHAN_TYPES.map((type) => ({
        type,
        items: cards[type],
        loading: loading[type],
        showCount: showCount[type],
        hasMore: cards[type].length > showCount[type],
      })),
    [cards, loading, showCount],
  );

  const handleShowMore = (type: DarshanType) => {
    setShowCount((previous) => ({ ...previous, [type]: previous[type] + 4 }));
  };

  return (
    <Box>
      <Stack spacing={3} sx={{ pb: 2 }}>
        <Card
          variant="outlined"
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "xl",
            borderColor: "divider",
            boxShadow: "lg",
            background: heroBackground,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: "none",
            }}
          />
          <CardContent sx={{ position: "relative" }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
                <Stack spacing={0.75}>
                  <Chip size="sm" variant="soft" color="neutral" sx={{ alignSelf: "flex-start", fontWeight: 600 }}>
                    Relish the Lord's beauty
                  </Chip>
                  <Typography level="h2" sx={{ fontWeight: 800, color: heroTitle }}>
                    Daily Darshan gallery
                  </Typography>
                  <Typography level="body-sm" sx={{ maxWidth: 680, color: heroBody }}>
                    Journey through the most recent darshans from Mangala, Sringar, and festival celebrations. Invite devotees to relish each transcendental moment.
                  </Typography>
                </Stack>
                <Button
                  component={RouterLink}
                  to="/darshan"
                  startDecorator={<CollectionsRoundedIcon />}
                  variant="soft"
                  color="primary"
                >
                  Steward today's uploads
                </Button>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: "lg",
                    background: panelWarm,
                    boxShadow: "sm",
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: "text.secondary" }}>
                      Recent highlights
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      View the latest seva
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                      Browse day-wise darshans and share them with the community.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: "lg",
                    background: panelCool,
                    boxShadow: "sm",
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: "text.secondary" }}>
                      Archive by type
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      Sringar • Mangala • Festival
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                      Open a full archive to revisit past darshan days.
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {sections.map(({ type, items, loading: sectionLoading, showCount: limit, hasMore }) => (
          <Stack key={type} spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              sx={{ alignItems: { xs: "flex-start", md: "center" } }}
            >
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Typography level="title-lg" sx={{ fontWeight: 700 }}>
                  {type} darshan highlights
                </Typography>
                <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 520 }}>
                  Relive the seva memories captured during recent {type.toLowerCase()} offerings. Click into any day to view the full gallery and share with your community.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  onClick={() => navigate(`/darshan-gallery/${type}`)}
                  endDecorator={<ArrowForwardRoundedIcon />}
                >
                  View full archive
                </Button>
              </Stack>
            </Stack>

            {sectionLoading && items.length === 0 ? (
              <Grid container spacing={2}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Grid key={index} xs={12} sm={6} lg={3}>
                    <Card variant="outlined" sx={{ borderRadius: "xl", p: 0, overflow: "hidden" }}>
                      <AspectRatio ratio="16/10">
                        <Skeleton variant="overlay" />
                      </AspectRatio>
                      <Stack spacing={1.5} sx={{ p: 2 }}>
                        <Skeleton width="60%" />
                        <Skeleton width="40%" />
                        <Skeleton width="30%" />
                      </Stack>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : items.length === 0 ? (
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "xl",
                  p: { xs: 2.5, md: 3 },
                  textAlign: "center",
                  gap: 1,
                  alignItems: "center",
                  boxShadow: "sm",
                }}
              >
                <Chip size="sm" variant="soft" color="primary" startDecorator={<CalendarTodayRoundedIcon fontSize="small" />}>
                  Awaiting darshan
                </Chip>
                <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                  No {type.toLowerCase()} darshan captured yet.
                </Typography>
                <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 360 }}>
                  Encourage photographers to upload today’s {type.toLowerCase()} darshan so devotees around the world can relish the Lord’s beauty.
                </Typography>
                <Button
                  size="sm"
                  variant="soft"
                  color="primary"
                  component={RouterLink}
                  to="/darshan"
                >
                  Open upload workspace
                </Button>
              </Card>
            ) : (
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  {items.slice(0, limit).map(({ date, cover }) => {
                    const formatted = dayjs(date).format("MMM DD, YYYY");
                    const subtitle = dayjs(date).format("dddd");
                    return (
                      <Grid key={`${type}-${date}`} xs={12} sm={6} lg={3}>
                        <Card
                          variant="outlined"
                          onClick={() => navigate(`/darshan-gallery/${type}/${date}`)}
                          sx={{
                            borderRadius: "xl",
                            p: 0,
                            overflow: "hidden",
                            cursor: "pointer",
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            boxShadow: "sm",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: "lg",
                            },
                          }}
                        >
                          <AspectRatio ratio="16/10">
                            {cover.mediaType === "image" ? (
                              <Box
                                component="img"
                                src={cover.url}
                                alt={cover.title || `${type} darshan ${formatted}`}
                                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <Box component="video" src={cover.url} preload="metadata" sx={{ width: "100%", height: "100%" }} />
                            )}
                          </AspectRatio>
                          <Stack spacing={1.5} sx={{ p: 2 }}>
                            <Stack spacing={0.5}>
                              <Chip size="sm" variant="soft" color="primary">
                                {formatted}
                              </Chip>
                              <Typography level="title-md" sx={{ fontWeight: 700 }}>
                                {type} darshan
                              </Typography>
                              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                                {subtitle} • Relive the transcendental aura and share with families who could not attend in person.
                              </Typography>
                            </Stack>
                            <Button
                              size="sm"
                              variant="soft"
                              color="primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/darshan-gallery/${type}/${date}`);
                              }}
                            >
                              View darshan gallery
                            </Button>
                          </Stack>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                {hasMore ? (
                  <Button
                    variant="outlined"
                    color="neutral"
                    onClick={() => handleShowMore(type)}
                    sx={{ alignSelf: { xs: "stretch", sm: "center" }, maxWidth: 260 }}
                  >
                    Load more darshans
                  </Button>
                ) : null}
              </Stack>
            )}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
